/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kSPRestCoreOData - kSPRestCore's OData answer readers and url / value helpers.
 * Split out of kSPRestCore.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kSPRestCore keeps every member as a same-signature delegating member.
 */

import { getCoreUtils, n0, nz, kNotEmptyString } from "./kCoreHandler";
import { kCollectionRowsCore } from "./kCollectionRowsCore";
import { kODataStringCore } from "./kODataStringCore";
import type { kSPRestCore } from "./kSPRestCore";
import { SPItemUpdateObj } from "./kSPItemUpdateObj";

export class kSPRestCoreOData {
    // ── Collection-response helpers (OData dialect independent) ──────────────

    /**
     * The row array of ANY SharePoint collection response, whatever OData dialect the request
     * negotiated:
     *   odata=verbose      { d: { results: [ … ], __next: "…" } }
     *   odata=minimal/full { value: [ … ], "odata.nextLink": "…" }
     *   odata=nometadata   { value: [ … ], "@odata.nextLink": "…" }
     * Hand-rolling this per call site is how a caller silently reads only the first page of a
     * 40 000-file library — an under-count that looks like a valid answer.
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.CollectionRows - its gate, try/catch and handler label "kSPRestCore.CollectionRows" stay verbatim, so error reports are unchanged.
    public static CollectionRows = (Self: typeof kSPRestCore, Data: unknown): any[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            if (Data === null || typeof Data !== "object") { return []; }
            const Obj: Record<string, unknown> = Data as Record<string, unknown>;
            const D: unknown = Obj.d;
            if (D !== null && typeof D === "object") {
                const Results: unknown = (D as Record<string, unknown>).results;
                if (Array.isArray(Results)) { return Results as any[]; }
            }
            if (Array.isArray(Obj.value)) { return Obj.value as any[]; }
            return kCollectionRowsCore.Expanded(Data, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.CollectionRows");
            return [];
        }
    };

    /**
     * Is this value a collection `CollectionRows` can actually read rows out of?
     *
     * THE POINT IS TO TELL "EMPTY" APART FROM "NOT A COLLECTION AT ALL", which `CollectionRows`
     * cannot: it answers `[]` for both, by design (a fail-safe default), and a caller that treats
     * that `[]` as an answer records "this container holds nothing".
     *
     * The shape that makes this urgent is `odata=verbose`'s **`{"__deferred":{"uri":"…"}}`** — what
     * SharePoint returns for a navigation property that was NOT expanded. It is a present, non-null
     * object with no rows and no `__next`, so a null-check passes it, `CollectionNextLink` says
     * "complete" and `CollectionRows` says "empty". Three correct-looking answers composing into a
     * confident lie. kSPWebStructureRead depends on this distinction to decide whether its
     * `$expand` actually happened; getting it wrong deletes every library of a web from MOSE's
     * catalogue, which is what the Select tab offers an operator as movable content.
     *
     * Fail-closed: an unrecognised shape is NOT a collection, so a caller falls back to reading it
     * the long way rather than believing an emptiness nobody measured.
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.IsCollection - its gate, try/catch and handler label "kSPRestCore.IsCollection" stay verbatim, so error reports are unchanged.
    public static IsCollection = (Self: typeof kSPRestCore, Data: unknown): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (Array.isArray(Data)) { return true; }
            if (Data === null || typeof Data !== "object") { return false; }
            const Obj: Record<string, unknown> = Data as Record<string, unknown>;
            const D: unknown = Obj.d;
            if (D !== null && typeof D === "object") {
                if (Array.isArray((D as Record<string, unknown>).results)) { return true; }
            }
            if (Array.isArray(Obj.value)) { return true; }
            if (Array.isArray(Obj.results)) { return true; }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.IsCollection");
            return false;
        }
    };

    /**
     * The ABSOLUTE continuation URL for the next page of a collection, or "" when this is the last
     * page. Feed it back through `RestExecuteExternal(NextLink, …, TargetWeb = "")` — with an empty
     * target the executor uses the URL verbatim instead of prefixing a site, which is exactly what
     * an absolute link needs. Fail-safe: an unrecognised shape returns "" (stop paging visibly
     * rather than build a wrong URL or loop).
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.CollectionNextLink - its gate, try/catch and handler label "kSPRestCore.CollectionNextLink" stay verbatim, so error reports are unchanged.
    public static CollectionNextLink = (Self: typeof kSPRestCore, Data: unknown): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Data === null || typeof Data !== "object") { return ""; }
            const Obj: Record<string, unknown> = Data as Record<string, unknown>;
            const D: unknown = Obj.d;
            if (D !== null && typeof D === "object") {
                const VerboseNext: unknown = (D as Record<string, unknown>).__next;
                if (typeof VerboseNext === "string" && VerboseNext !== "") { return VerboseNext; }
            }
            // An EXPANDED collection is handed in already unwrapped — `d.Lists`, not `d` — so its
            // verbose paging marker sits at the TOP level here, with no `d` above it. Without this
            // branch a truncated $expand answers "" (= complete) and the caller records a short
            // collection as the whole thing, which for the tenant inventory means silently deleting
            // libraries from the catalogue of exactly the biggest sites. Added 2026-08-14 for
            // kSPWebStructureRead; no existing caller passes an unwrapped collection, so no
            // behaviour changes for them.
            const ExpandedNext: unknown = Obj.__next;
            if (typeof ExpandedNext === "string" && ExpandedNext !== "") { return ExpandedNext; }
            const Annotated: unknown = Obj["@odata.nextLink"];
            if (typeof Annotated === "string" && Annotated !== "") { return Annotated; }
            const Plain: unknown = Obj["odata.nextLink"];
            if (typeof Plain === "string" && Plain !== "") { return Plain; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.CollectionNextLink");
            return "";
        }
    };

    /** Row ETag across OData dialects. DOCUMENTED EXCEPTION: pure reader, callers own gates and faults.
     * Batch prefers its historical odata.etag spelling; other callers prefer verbose metadata. */
    public static ETagOf = (Self: typeof kSPRestCore, Item: any, PreferMinimal: boolean = false): string => {
        const ETag: string | null = Self.ETagOrNullOf(Item, PreferMinimal);
        if (ETag === null) { return ""; }
        return ETag;
    };

    /** DOCUMENTED EXCEPTION: pure nullable reader distinguishes absent from an explicit empty ETag. */
    public static ETagOrNullOf = (Self: typeof kSPRestCore, Item: any, PreferMinimal: boolean = false): string | null => {
        if (Item === null || typeof Item !== "object") { return null; }
        if (PreferMinimal && typeof Item["odata.etag"] === "string") { return Item["odata.etag"]; }
        const Meta: any = Item.__metadata;
        if (Meta !== null && typeof Meta === "object" && typeof Meta.etag === "string") { return Meta.etag; }
        if (typeof Item["@odata.etag"] === "string") { return Item["@odata.etag"]; }
        if (typeof Item["odata.etag"] === "string") { return Item["odata.etag"]; }
        return null;
    };

    /** The single entity of a non-collection response (unwraps the verbose `d` envelope). */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.EntityOf - its gate, try/catch and handler label "kSPRestCore.EntityOf" stay verbatim, so error reports are unchanged.
    public static EntityOf = (Self: typeof kSPRestCore, Data: unknown): Record<string, any> => {
        if (getCoreUtils().KErrorMode) { return {}; }
        try {
            const Entity: Record<string, any> | null = Self.EntityOrNullOf(Data);
            if (Entity === null) { return {}; }
            return Entity;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.EntityOf");
            return {};
        }
    };

    /** Nullable entity variant for batch responses; no gate because wrappers own their existing policy.
     * DOCUMENTED EXCEPTION: pure response read; malformed getters throw to the caller's error boundary. */
    public static EntityOrNullOf = (Self: typeof kSPRestCore, Data: unknown): Record<string, any> | null => {
        if (Data === null || typeof Data !== "object") { return null; }
        const Obj: Record<string, unknown> = Data as Record<string, unknown>;
        const D: unknown = Obj.d;
        if (D !== null && typeof D === "object") { return D as Record<string, any>; }
        return Obj;
    };

    /** REST row identifier: ID takes precedence, then Id; numeric strings retain n0 coercion.
     * DOCUMENTED EXCEPTION: scalar read, ungated like the existing callers' n0 sequence; malformed null rows throw into the caller's error boundary. */
    public static ItemIdOf = (Self: typeof kSPRestCore, Item: Record<string, any>): number => {
        let Id: number = n0(Item.ID);
        if (Id === 0) { Id = n0(Item.Id); }
        return Id;
    };

    /**
     * Encode a server-relative path for use INSIDE GetFileByServerRelativeUrl('<literal>') /
     * GetFolderByServerRelativeUrl('<literal>'). The executor sends the URL verbatim (Url =
     * TargetWeb + RestCall, no encoding), so each segment must be percent-encoded (a space → %20)
     * and each OData single quote doubled. Segments are encoded individually so the "/" separators
     * survive.
     * LIVE-VERIFIED 2026-07-20: the raw call 400'd for the Swedish library "Delade dokument";
     * the %20-encoded form read/wrote/verified/deleted the file correctly.
     *
     * ForODataLiteral (default true, so every existing caller is unchanged) controls the quote half
     * ONLY. Pass FALSE when the result is a BROWSABLE URL rather than an OData string literal — a
     * plain link must keep one apostrophe, since '' is a doubling SharePoint only undoes inside
     * ...ByServerRelativeUrl('...'). Doubling it in a href yields a url to a file that does not exist.
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.EncodePath - its gate, try/catch and handler label "kSPRestCore.EncodePath" stay verbatim, so error reports are unchanged.
    public static EncodePath = (Self: typeof kSPRestCore, Path: string, ForODataLiteral: boolean = true): string => {
        if (getCoreUtils().KErrorMode) { return Path; }
        try {
            const Segments: string[] = Path.split("/");
            const Encoded: string[] = [];
            for (const Segment of Segments) {
                // encodeURIComponent handles space/#/% etc.; it leaves "'" intact, so double it
                // afterwards for the OData string literal (SharePoint decodes '' back to ').
                const Encodedsegment: string = encodeURIComponent(Segment);
                if (ForODataLiteral === false) { Encoded.push(Encodedsegment); continue; }
                Encoded.push(Encodedsegment.split("'").join("''"));
            }
            return Encoded.join("/");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.EncodePath");
            return Path;
        }
    };

    /**
     * True only when `Url` STARTS with "http" in any case - an absolute address that must NOT be
     * prefixed with a web URL. Since 2026-10-10 (R6c, owner decision): the call sites it replaced
     * asked `Url.indexOf("http") === -1`, a substring test, so a server-relative path that merely
     * CONTAINED "http" (".../http-notes.txt") went out without its web prefix. Pure.
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.IsAbsoluteHttpUrl - its gate, try/catch and handler label "kSPRestCore.IsAbsoluteHttpUrl" stay verbatim, so error reports are unchanged.
    public static IsAbsoluteHttpUrl = (Self: typeof kSPRestCore, Url: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (typeof Url !== "string") { return false; }
            return Url.substring(0, 4).toLowerCase() === "http";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.IsAbsoluteHttpUrl");
            return false;
        }
    };

    /**
     * The list address by title, "/_api/web/lists/getbytitle('<title>')", for a caller to append its
     * own "/items..." part. The title's apostrophe is doubled for the OData literal and the WHOLE title
     * is then encodeURIComponent'ed (so "/" becomes %2F, unlike EncodePath). This is the form that
     * kMailOutbox, kApprovalStoreCore and kSettingsListCore (twice) each wrote inline (2026-10-09).
     * Since 2026-10-10 (R6) also the address of the one-step-quoted sites it replaced in kSPListCore,
     * kSPItemCore, kSPListPermissionsCore, ReUsableTS kSPRest2 and ChatListPersistence - each had
     * EITHER the apostrophe doubled OR the title encoded, never both. Append the rest ("/items...").
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.ListByTitleUrl - its gate, try/catch and handler label "kSPRestCore.ListByTitleUrl" stay verbatim, so error reports are unchanged.
    public static ListByTitleUrl = (Self: typeof kSPRestCore, Title: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return "/_api/web/lists/getbytitle('" + encodeURIComponent(Title.replace(/'/g, "''")) + "')";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.ListByTitleUrl");
            return "";
        }
    };

    // ── Pure utility methods ─────────────────────────────────────────────────

    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.ServerRootURL - its gate, try/catch and handler label "kSPRestCore.ServerRootURL" stay verbatim, so error reports are unchanged.
    public static ServerRootURL = (Self: typeof kSPRestCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return Self.URLs.CurrSiteFullURL.replace(
                Self.URLs.WebRelativeURL, ""
            );
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.ServerRootURL");
            return "";
        }
    };

    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.AddUpdateValueObj - its gate, try/catch and handler label "kSPRestCore.AddUpdateValueObj" stay verbatim, so error reports are unchanged.
    public static AddUpdateValueObj = (Self: typeof kSPRestCore, InternalFieldName: string, FieldValue: string): SPItemUpdateObj => {
        if (getCoreUtils().KErrorMode) { return new SPItemUpdateObj(); }
        try {
            const UpdateObj: SPItemUpdateObj = new SPItemUpdateObj();
            UpdateObj.FieldName = InternalFieldName;
            UpdateObj.FieldValue = FieldValue;
            return UpdateObj;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.AddUpdateValueObj");
            return new SPItemUpdateObj();
        }
    };

    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.MultiChoiceFieldValueSave - its gate, try/catch and handler label "kSPRestCore.MultiChoiceFieldValueSave" stay verbatim, so error reports are unchanged.
    public static MultiChoiceFieldValueSave = (Self: typeof kSPRestCore, ChosenValues: string[]): any => {
        if (getCoreUtils().KErrorMode) { return {}; }
        try {
            return {
                __metadata: { type: "Collection(Edm.String)" },
                results: ChosenValues,
            };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.MultiChoiceFieldValueSave");
            return {};
        }
    };

    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.FieldUrlValue - its gate, try/catch and handler label "kSPRestCore.FieldUrlValue" stay verbatim, so error reports are unchanged.
    public static FieldUrlValue = (Self: typeof kSPRestCore, URL: string, Description?: string): object | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            let ResolvedDescription: string = nz(Description);
            if (!kNotEmptyString(ResolvedDescription)) {
                ResolvedDescription = URL;
            }
            const FieldValue: any = {
                __metadata: { type: "SP.FieldUrlValue" },
                Description: ResolvedDescription,
                Url: URL,
            };
            return FieldValue;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.FieldUrlValue");
            return null;
        }
    };

    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.UserObjectSelectAndExpandString - its gate, try/catch and handler label "kSPRestCore.UserObjectSelectAndExpandString" stay verbatim, so error reports are unchanged.
    public static UserObjectSelectAndExpandString = (Self: typeof kSPRestCore, UserFieldInternalNames: string[]): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let SelectString: string = "";
            let CurrIntName: string = "";
            for (const FieldName of UserFieldInternalNames) {
                CurrIntName = FieldName;
                SelectString +=
                    CurrIntName + "%2FID," +
                    CurrIntName + "%2FTitle," +
                    CurrIntName + "/Name,";
            }
            SelectString = SelectString.substring(0, SelectString.length - 1);
            return SelectString;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.UserObjectSelectAndExpandString");
            return "";
        }
    };

    /**
     * SharePoint OData single-quote literal escaping: null/undefined -> "", single quote ' -> doubled ''
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.EscapeODataFilter - its gate, try/catch and handler label "kSPRestCore.EscapeODataFilter" stay verbatim, so error reports are unchanged.
    public static EscapeODataFilter = (Self: typeof kSPRestCore, Value: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Value === null || Value === undefined) { return ""; }
            return kODataStringCore.Quote(nz(Value));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.EscapeODataFilter");
            return "";
        }
    };
}
